import React, { useEffect, useState } from 'react'
import { useCompanyStore } from '@store'
import { Building2, Plus, Eye, Edit2, Trash2, RotateCcw, PauseCircle, CheckCircle, TrendingUp, Building, Calendar, XCircle, Filter, ArrowUpDown } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import CompanyForm from './CompanyForm'
import ViewCompanyModal from '@components/features/companies/ViewCompanyModal'
import './Companies.css'

/**
 * Format date to readable string
 */
const formatDate = (dateString) => {
	if (!dateString) return '-'
	const date = new Date(dateString)
	return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Companies Management Page (Super Admin Only)
 *
 * Complete company management interface with:
 * - List view with DataTable
 * - Search and filtering by package, status
 * - View/Edit/Delete/Restore/Activate/Suspend operations
 * - Statistics dashboard
 * - Pagination
 * - Responsive design
 *
 * Features:
 * - Full CRUD operations
 * - Real-time search
 * - Advanced filtering (package, status)
 * - Company activation/suspension
 * - Soft delete with restore
 * - Confirmation dialogs
 * - Loading states
 * - Empty states
 * - Error handling
 */
const Companies = () => {
	const {
		companies,
		selectedCompany,
		loading,
		filters,
		pagination,
		stats,
		packages,
		fetchCompanies,
		fetchCompanyById,
		fetchPackages,
		selectCompany,
		deleteCompany,
		restoreCompany,
		activateCompany,
		suspendCompany,
		setSearch,
		setPackageFilter,
		setStatusFilter,
		setPage,
		nextPage,
		prevPage,
	} = useCompanyStore()

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [actionType, setActionType] = useState(null) // 'delete', 'restore', 'activate', 'suspend'
	const [selectedCompanyForAction, setSelectedCompanyForAction] = useState(null)
	const [editingCompany, setEditingCompany] = useState(null)
	const [viewingCompanyId, setViewingCompanyId] = useState(null)
	const [viewModalRefreshTrigger, setViewModalRefreshTrigger] = useState(0)

	// Fetch companies and packages on mount
	useEffect(() => {
		fetchCompanies()
		fetchPackages()
	}, [])

	/**
	 * Handle create company
	 */
	const handleCreate = () => {
		selectCompany(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view company
	 */
	const handleView = (company) => {
		selectCompany(company)
		setViewingCompanyId(company.id)
		setShowViewModal(true)
	}

	/**
	 * Handle edit company
	 */
	const handleEdit = async (company) => {
		try {
			// Fetch full company details including users for dropdown
			const fullCompany = await fetchCompanyById(company.id)
			setEditingCompany(fullCompany)
			setShowEditModal(true)
		} catch (error) {
			console.error('Failed to fetch company details:', error)
		}
	}

	/**
	 * Handle delete company
	 */
	const handleDelete = (company) => {
		setSelectedCompanyForAction(company)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore company
	 */
	const handleRestore = (company) => {
		setSelectedCompanyForAction(company)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle activate company
	 */
	const handleActivate = (company) => {
		setSelectedCompanyForAction(company)
		setActionType('activate')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle suspend company
	 */
	const handleSuspend = (company) => {
		setSelectedCompanyForAction(company)
		setActionType('suspend')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle send activation email
	 */
	const handleSendActivation = async (company) => {
		try {
			const { apiClient } = await import('@api')
			const response = await apiClient.post(`/company-activation/send-email/${company.id}`)

			if (response.status === 200) {
				alert(`Activation email sent successfully to ${company.compliance_manager?.email || 'compliance manager'}`)
				// Refresh company data
				await fetchCompanies()
				// Trigger view modal refresh if open
				if (showViewModal && viewingCompanyId === company.id) {
					setViewModalRefreshTrigger(prev => prev + 1)
				}
			}
		} catch (error) {
			console.error('Failed to send activation email:', error)
			alert(error.response?.data?.message || 'Failed to send activation email. Please try again.')
		}
	}

	/**
	 * Handle resend activation email
	 */
	const handleResendActivation = async (company) => {
		const confirmed = window.confirm(
			`Are you sure you want to resend the activation email to ${company.compliance_manager?.email || 'the compliance manager'}? The previous activation link will be invalidated.`
		)

		if (confirmed) {
			await handleSendActivation(company)
		}
	}

	/**
	 * Confirm action (delete/restore/activate/suspend)
	 */
	const confirmAction = async () => {
		if (!selectedCompanyForAction) return

		try {
			if (actionType === 'delete') {
				await deleteCompany(selectedCompanyForAction.id)
			} else if (actionType === 'restore') {
				await restoreCompany(selectedCompanyForAction.id)
			} else if (actionType === 'activate') {
				await activateCompany(selectedCompanyForAction.id)
			} else if (actionType === 'suspend') {
				await suspendCompany(selectedCompanyForAction.id)
			}

			setShowConfirmDialog(false)
			setSelectedCompanyForAction(null)
			setActionType(null)

			// Notify view modal to refresh data if it's open
			if (showViewModal && viewingCompanyId === selectedCompanyForAction.id) {
				// The view modal will refetch automatically via the updated companies list
				// But we'll trigger a manual refresh by toggling a refresh flag
				setViewModalRefreshTrigger(prev => prev + 1)
			}
		} catch (error) {
			console.error('Action failed:', error)
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedCompanyForAction(null)
		setActionType(null)
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status) => {
		if (status === 'active') {
			return <span className="badge badge--active">Active</span>
		} else if (status === 'trial') {
			return <span className="badge badge--trial">Trial</span>
		} else if (status === 'pending') {
			return <span className="badge badge--pending">Pending</span>
		} else if (status === 'suspended') {
			return <span className="badge badge--suspended">Suspended</span>
		} else if (status === 'cancelled') {
			return <span className="badge badge--cancelled">Cancelled</span>
		}
		return <span className="badge badge--inactive">Unknown</span>
	}

	/**
	 * Get action confirmation details
	 */
	const getConfirmDetails = () => {
		if (!selectedCompanyForAction || !actionType) return {}

		const companyName = selectedCompanyForAction.name

		const details = {
			delete: {
				title: 'Archive Company?',
				message: `Are you sure you want to archive "${companyName}"? This will cancel the subscription and soft delete the company, but data will be preserved.`,
				confirmText: 'Archive',
				variant: 'danger',
			},
			restore: {
				title: 'Restore Company?',
				message: `Are you sure you want to restore "${companyName}"? This will reactivate the company account (status: suspended).`,
				confirmText: 'Restore',
				variant: 'info',
			},
			activate: {
				title: 'Activate Company?',
				message: `Are you sure you want to activate "${companyName}"? This will enable full access to the company account.`,
				confirmText: 'Activate',
				variant: 'success',
			},
			suspend: {
				title: 'Suspend Company?',
				message: `Are you sure you want to suspend "${companyName}"? This will temporarily disable access to the company account.`,
				confirmText: 'Suspend',
				variant: 'warning',
			},
		}

		return details[actionType] || {}
	}

	// Define table columns
	const columns = [
		{
			key: 'name',
			label: 'Company Name',
			sortable: true,
			render: (company) => (
				<div className="company-name-cell">
					<div className="company-name-cell__icon">
						<Building2 size={20} />
					</div>
					<div className="company-name-cell__content">
						<div className="company-name-cell__name">{company.name}</div>
						<div className="company-name-cell__slug">{company.slug}</div>
					</div>
				</div>
			),
		},
		{
			key: 'compliance_manager',
			label: 'Compliance Manager',
			render: (company) => (
				<div className="manager-cell">
					{company.compliance_manager_name || '-'}
				</div>
			),
		},
		{
			key: 'email',
			label: 'Email',
			sortable: true,
			render: (company) => (
				<div className="email-cell">
					{company.email}
				</div>
			),
		},
		{
			key: 'package',
			label: 'Package',
			sortable: true,
			render: (company) => (
				<div className="package-cell">
					<span className="badge badge--primary">{company.package_name || '-'}</span>
				</div>
			),
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (company) => getStatusBadge(company.status),
		},
		{
			key: 'created_at',
			label: 'Created',
			sortable: true,
			render: (company) => (
				<div className="date-cell">
					{formatDate(company.created_at)}
				</div>
			),
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (company) => company.status !== 'cancelled' && !company.deleted_at,
		},
		{
			label: 'Activate',
			icon: <CheckCircle size={16} />,
			onClick: handleActivate,
			variant: 'success',
			show: (company) => company.status !== 'active' && !company.deleted_at,
		},
		{
			label: 'Suspend',
			icon: <PauseCircle size={16} />,
			onClick: handleSuspend,
			variant: 'warning',
			show: (company) => company.status !== 'suspended' && company.status !== 'cancelled' && !company.deleted_at,
		},
		{
			label: 'Archive',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (company) => !company.deleted_at,
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (company) => company.deleted_at,
		},
	]

	const confirmDetails = getConfirmDetails()

	return (
		<div className="companies-page">
			{/* Page Header with Breadcrumbs */}
			<PageHeader
				title="Companies"
				description="Manage companies, subscriptions, and settings"
				breadcrumbs={[{ label: 'Companies' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={20} />
						<span>Add Company</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<Building2 size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Companies</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<CheckCircle size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Active</div>
						<div className="stat-card__value">{stats.active}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--info">
						<Calendar size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Trial</div>
						<div className="stat-card__value">{stats.trial}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--warning">
						<PauseCircle size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Suspended</div>
						<div className="stat-card__value">{stats.suspended}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={filters.search}
					onChange={setSearch}
					placeholder="Search companies by name..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={filters.package_id}
						onChange={(e) => setPackageFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Packages' },
							...packages.map(pkg => ({
								value: pkg.id,
								label: pkg.title || pkg.name
							}))
						]}
					/>

					<Select
						value={filters.status}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'trial', label: 'Trial' },
							{ value: 'pending', label: 'Pending' },
							{ value: 'suspended', label: 'Suspended' },
							{ value: 'cancelled', label: 'Cancelled' },
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={companies}
				actions={actions}
				loading={loading}
				emptyMessage="No companies found. Create your first company to get started."
				onRowClick={handleView}
			/>

			{/* Pagination */}
			{pagination.totalPages > 1 && (
				<div className="pagination">
					<div className="pagination__info">
						Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
					</div>
					<div className="pagination__controls">
						<Button
							variant="secondary"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={nextPage}
							disabled={pagination.page >= pagination.totalPages || loading}
							size="small"
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			{showCreateModal && (
				<CompanyForm
					onClose={() => setShowCreateModal(false)}
				/>
			)}

			{/* Edit Modal */}
			{showEditModal && editingCompany && (
				<CompanyForm
					company={editingCompany}
					onClose={() => {
						setShowEditModal(false)
						setEditingCompany(null)
					}}
				/>
			)}

			{/* Confirm Dialog (Delete/Restore/Activate/Suspend) */}
			{showConfirmDialog && selectedCompanyForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={confirmDetails.title}
					message={confirmDetails.message}
					confirmText={confirmDetails.confirmText}
					variant={confirmDetails.variant}
				/>
			)}

			{/* View Company Modal */}
			{showViewModal && viewingCompanyId && (
				<ViewCompanyModal
					isOpen={showViewModal}
					onClose={() => {
						setShowViewModal(false)
						setViewingCompanyId(null)
					}}
					companyId={viewingCompanyId}
					refreshTrigger={viewModalRefreshTrigger}
					onEdit={(company) => handleEdit(company)}
					onActivate={(company) => handleActivate(company)}
					onSuspend={(company) => handleSuspend(company)}
					onArchive={(company) => handleDelete(company)}
					onRestore={(company) => handleRestore(company)}
					onSendActivation={(company) => handleSendActivation(company)}
					onResendActivation={(company) => handleResendActivation(company)}
				/>
			)}
		</div>
	)
}

export default Companies
